/**
 * « Caméras » — écran staff (UniFi Protect, tenant Mayday).
 *
 * - Live view : snapshot rafraîchi toutes les ~2,5 s (proxy backend, blob),
 *   visibility-gated. Compteur « Personnes » (heure + jour) sous la vidéo.
 * - Capture à la demande (📸) + carrousel des dernières captures manuelles.
 * - « Client en attente » : bandeau si une détection en cours dépasse le seuil
 *   serveur (5 min par défaut), badge ⏱️ sur les heures passées concernées.
 * - Activité récente : détections groupées par heure (24 h) avec vignette.
 * - Timelapse de la veille.
 *
 * Multi-caméras : la liste vient de /pos/camera/status (`cameras[]`) ; aujourd'hui
 * 1 entrée (« La salle »). Le front utilise `cameras[0]` par défaut.
 *
 * Tout passe par /pos/camera/*. RGPD : comptage anonyme uniquement.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { isCurrentUserAdmin } from '../../utils/auth';

const LIVE_POLL_MS = 2_500;
const COUNT_POLL_MS = 30_000;
/** Cadence du bandeau « client en attente » — calée sur le checker backend. */
const DWELL_POLL_MS = 15_000;
const RECENT_POLL_MS = 60_000;
const TIMELAPSE_FPS = 8;

/** `YYYY-MM-DD` en Europe/Paris, décalé de `offsetDays`. */
function parisDate(offsetDays = 0): string {
  const now = Date.now() + offsetDays * 86_400_000;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(now));
}
/** `HH:MM` Europe/Paris. */
function hm(ms: number): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
  }).format(new Date(ms));
}
/** Durée d'attente en minutes pour l'affichage (plancher 1, jamais « 0 min »). */
function dwellMin(seconds: number): number {
  return Math.max(1, Math.floor(seconds / 60));
}
/** « 14h » depuis une clé `YYYY-MM-DDTHH` (+ « hier » si pas aujourd'hui). */
function hourLabel(hourKey: string): string {
  const [d, h] = hourKey.split('T');
  const suffix = d === parisDate(0) ? '' : d === parisDate(-1) ? ' (hier)' : ` (${d.slice(8)}/${d.slice(5, 7)})`;
  return `${Number(h)}h${suffix}`;
}

/** Décode une clé VAPID base64url en Uint8Array pour applicationServerKey. */
function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const pushSupported = typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

type PushState = 'unavailable' | 'off' | 'on' | 'denied' | 'busy';

interface TypeCounts { person: number }
interface HourBucket { hour: number; count: number }
interface DetectionsCount {
  hour: TypeCounts;
  day: TypeCounts;
  asOf: number;
  // Contexte business (backend récent) — optionnels : un backend antérieur ne les renvoie pas.
  previousHourSameDay?: number;
  peakHour?: HourBucket | null;
  hourlySeries?: HourBucket[];
}
interface Frame { id: string; capturedAt: number; url: string }
interface CameraRef { id: string; name: string; unifiCameraId: string }
interface RecentGroup {
  hourKey: string; count: number; first: number; last: number; thumbUrl: string | null;
  /** Plus longue attente mesurée dans l'heure (backend récent ; null si non mesurée). */
  maxDwellSeconds?: number | null;
}
/** Détections en cours au-delà du seuil d'attente (/pos/camera/dwell-status). */
interface DwellStatus {
  thresholdSeconds: number;
  waiting: Array<{ detectedAt: number; dwellSeconds: number }>;
  asOf: number;
}
interface Thumb { id: string; capturedAt: number; blob: string }
interface RecentRow extends RecentGroup { blob: string | null }

/**
 * Sparkline d'affluence : une barre par heure, la dernière (heure courante) en
 * bleu plein, les autres en bleu clair. SVG maison, zéro dépendance.
 */
function AffluenceSparkline({ series }: { series: HourBucket[] }): React.ReactElement | null {
  if (!series.length) return null;
  const max = Math.max(1, ...series.map((b) => b.count));
  const W = 100, H = 32, n = series.length, gap = 2.5;
  const bw = (W - gap * (n - 1)) / n;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: 34, display: 'block' }}
      role="img"
      aria-label={`Affluence des ${n} dernières heures`}
    >
      {series.map((b, i) => {
        const h = Math.max(1.5, (b.count / max) * H);
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={H - h}
            width={bw}
            height={h}
            rx={1}
            fill={i === n - 1 ? '#1464B4' : '#a9cbec'}
          />
        );
      })}
    </svg>
  );
}

/** Delta « vs hier même heure » avec flèche colorée (↑ vert / ↓ orange / → gris). */
function HourDelta({ now, prev }: { now: number; prev: number }): React.ReactElement {
  const d = now - prev;
  const up = d > 0, down = d < 0;
  const color = up ? '#1c8a4e' : down ? '#c9741a' : '#8a8a96';
  const arrow = up ? '↑' : down ? '↓' : '→';
  const val = d === 0 ? '=' : `${up ? '+' : ''}${d}`;
  return (
    <span style={{ color, fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap' }}>
      {arrow} {val} vs hier même heure
    </span>
  );
}

export function StaffCamera(): React.ReactElement {
  const admin = isCurrentUserAdmin();

  const [configured, setConfigured] = useState<boolean | null>(null);
  const [cameraName, setCameraName] = useState<string>('La salle');
  const [liveSrc, setLiveSrc] = useState<string | null>(null);
  const [liveError, setLiveError] = useState(false);
  const [count, setCount] = useState<DetectionsCount | null>(null);
  const [dwell, setDwell] = useState<DwellStatus | null>(null);

  const [capturing, setCapturing] = useState(false);
  const [pushState, setPushState] = useState<PushState>('unavailable');
  const [prePrompt, setPrePrompt] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [onDemand, setOnDemand] = useState<Thumb[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);

  // Timelapse
  const [tlOpen, setTlOpen] = useState(false);
  const [tlFrames, setTlFrames] = useState<string[]>([]);
  const [tlIdx, setTlIdx] = useState(0);
  const [tlLoading, setTlLoading] = useState(false);
  const [tlEmpty, setTlEmpty] = useState(false);

  // Preview animée du timelapse d'hier (mini-diaporama auto-play sur le bouton).
  const [tlPreview, setTlPreview] = useState<string[]>([]);
  const [tlPreviewIdx, setTlPreviewIdx] = useState(0);

  const liveUrlRef = useRef<string | null>(null);
  const tlUrlsRef = useRef<string[]>([]);
  const tlPreviewUrlsRef = useRef<string[]>([]);
  const onDemandUrlsRef = useRef<string[]>([]);
  const recentUrlsRef = useRef<string[]>([]);

  /** apiFetch d'une image authentifiée → object URL (ou null). */
  const fetchBlob = useCallback(async (path: string): Promise<string | null> => {
    try {
      const r = await apiFetch(path);
      if (!r.ok) return null;
      return URL.createObjectURL(await r.blob());
    } catch { return null; }
  }, []);

  // ── Live snapshot (blob, revoke à chaque frame). ──
  const fetchLive = useCallback(async () => {
    try {
      const res = await apiFetch('/pos/camera/live-snapshot');
      if (res.status === 503) { setConfigured(false); return; }
      if (!res.ok) { setLiveError(true); return; }
      const url = URL.createObjectURL(await res.blob());
      if (liveUrlRef.current) URL.revokeObjectURL(liveUrlRef.current);
      liveUrlRef.current = url;
      setLiveSrc(url); setLiveError(false); setConfigured(true);
    } catch { setLiveError(true); }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const res = await apiFetch('/pos/camera/detections-count');
      if (res.ok) setCount(await res.json());
    } catch { /* garde l'affichage précédent */ }
  }, []);

  // Qui attend en ce moment. Route récente : un backend antérieur renvoie 404,
  // on laisse alors `dwell` à null (aucun bandeau, aucun badge).
  const fetchDwell = useCallback(async () => {
    try {
      const res = await apiFetch('/pos/camera/dwell-status');
      if (res.ok) setDwell(await res.json());
    } catch { /* garde l'affichage précédent */ }
  }, []);

  const refreshOnDemand = useCallback(async () => {
    try {
      const res = await apiFetch('/pos/camera/ondemand-snapshots?limit=12');
      if (!res.ok) return;
      const rows: Frame[] = await res.json();
      const thumbs: Thumb[] = [];
      for (const r of rows) {
        const blob = await fetchBlob(r.url);
        if (blob) thumbs.push({ id: r.id, capturedAt: r.capturedAt, blob });
      }
      onDemandUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      onDemandUrlsRef.current = thumbs.map((t) => t.blob);
      setOnDemand(thumbs);
    } catch { /* ignore */ }
  }, [fetchBlob]);

  const refreshRecent = useCallback(async () => {
    try {
      const res = await apiFetch('/pos/camera/detections-recent?hours=24');
      if (!res.ok) return;
      const groups: RecentGroup[] = await res.json();
      const rows: RecentRow[] = [];
      for (const g of groups) {
        const blob = g.thumbUrl ? await fetchBlob(g.thumbUrl) : null;
        rows.push({ ...g, blob });
      }
      recentUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      recentUrlsRef.current = rows.map((r) => r.blob).filter((b): b is string => !!b);
      setRecent(rows);
    } catch { /* ignore */ }
  }, [fetchBlob]);

  // Charge une preview LÉGÈRE du timelapse d'hier : ~10 frames réparties sur la
  // journée, jouées en boucle sur le bouton pour signaler que c'est cliquable.
  const loadTimelapsePreview = useCallback(async () => {
    try {
      const res = await apiFetch(`/pos/camera/timelapse/${parisDate(-1)}`);
      if (!res.ok) return;
      const data: { frames: Frame[] } = await res.json();
      const frames = data.frames ?? [];
      if (!frames.length) return;
      const N = Math.min(10, frames.length);
      const step = frames.length / N;
      const picked = Array.from({ length: N }, (_, i) => frames[Math.floor(i * step)]);
      const urls: string[] = [];
      for (const f of picked) {
        const blob = await fetchBlob(f.url);
        if (blob) urls.push(blob);
      }
      if (!urls.length) return;
      tlPreviewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      tlPreviewUrlsRef.current = urls;
      setTlPreview(urls); setTlPreviewIdx(0);
    } catch { /* pas de preview → on garde le bouton texte simple */ }
  }, [fetchBlob]);

  const captureNow = useCallback(async () => {
    setCapturing(true);
    try {
      const res = await apiFetch('/pos/camera/snapshot', { method: 'POST' });
      if (res.ok) {
        setToast('Snapshot enregistré ✓');
        await refreshOnDemand();
      } else {
        setToast('Capture impossible');
      }
    } catch { setToast('Capture impossible'); }
    setCapturing(false);
    window.setTimeout(() => setToast(null), 2500);
  }, [refreshOnDemand]);

  // ── Alertes sécurité (Web Push). ──
  // Détecte l'état initial : push supporté ? backend configuré ? déjà abonné ?
  useEffect(() => {
    if (!pushSupported) { setPushState('unavailable'); return; }
    (async () => {
      try {
        const res = await apiFetch('/pos/camera/push/vapid-public-key');
        const data = res.ok ? await res.json() : { enabled: false };
        if (!data.enabled) { setPushState('unavailable'); return; }
        if (Notification.permission === 'denied') { setPushState('denied'); return; }
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setPushState(sub ? 'on' : 'off');
      } catch { setPushState('unavailable'); }
    })();
  }, []);

  const enablePush = useCallback(async () => {
    setPushState('busy');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState(perm === 'denied' ? 'denied' : 'off'); return; }
      const keyRes = await apiFetch('/pos/camera/push/vapid-public-key');
      const { publicKey } = await keyRes.json();
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey) as BufferSource,
      });
      const r = await apiFetch('/pos/camera/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub),
      });
      if (!r.ok) throw new Error('subscribe failed');
      setPushState('on');
      setToast('Alertes activées ✓');
      apiFetch('/pos/camera/push/test', { method: 'POST' }).catch(() => {});
    } catch {
      setPushState('off');
      setToast('Activation impossible');
    }
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // Désactivation : unsubscribe côté navigateur + DELETE en base.
  const disablePush = useCallback(async () => {
    setDisabling(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await apiFetch('/pos/camera/push/subscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setPushState('off');
      setToast('Alertes désactivées');
    } catch {
      setToast('Désactivation impossible');
    }
    setDisabling(false);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // Statut initial : configured + nom caméra.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/pos/camera/status');
        if (res.ok) {
          const s = await res.json();
          setConfigured(s.configured === true);
          const cams: CameraRef[] = s.cameras ?? [];
          if (cams[0]?.name) setCameraName(cams[0].name);
        } else setConfigured(false);
      } catch { setConfigured(false); }
    })();
  }, []);

  // Live + compteur + attente : polling visibility-gated.
  useEffect(() => {
    if (configured === false) return;
    fetchLive(); fetchCount(); fetchDwell();
    const liveTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !tlOpen) fetchLive();
    }, LIVE_POLL_MS);
    const countTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchCount();
    }, COUNT_POLL_MS);
    const dwellTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchDwell();
    }, DWELL_POLL_MS);
    return () => { clearInterval(liveTimer); clearInterval(countTimer); clearInterval(dwellTimer); };
  }, [configured, tlOpen, fetchLive, fetchCount, fetchDwell]);

  // Activité récente + carrousel : chargement initial + refresh périodique.
  useEffect(() => {
    if (configured === false) return;
    refreshRecent(); refreshOnDemand();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshRecent();
    }, RECENT_POLL_MS);
    return () => clearInterval(t);
  }, [configured, refreshRecent, refreshOnDemand]);

  // Preview timelapse : chargée une fois quand la caméra est configurée.
  useEffect(() => {
    if (configured === false) return;
    loadTimelapsePreview();
  }, [configured, loadTimelapsePreview]);

  // Boucle du mini-diaporama de preview (~2,5 fps, gelée quand le modal est ouvert).
  useEffect(() => {
    if (tlPreview.length < 2 || tlOpen) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') setTlPreviewIdx((i) => (i + 1) % tlPreview.length);
    }, 400);
    return () => clearInterval(t);
  }, [tlPreview, tlOpen]);

  // Nettoyage des blob URLs au démontage.
  useEffect(() => () => {
    if (liveUrlRef.current) URL.revokeObjectURL(liveUrlRef.current);
    tlUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    tlPreviewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    onDemandUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    recentUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  // ── Timelapse d'hier. ──
  const openTimelapse = useCallback(async () => {
    setTlOpen(true); setTlLoading(true); setTlEmpty(false); setTlIdx(0);
    try {
      const res = await apiFetch(`/pos/camera/timelapse/${parisDate(-1)}`);
      const data: { frames: Frame[] } = res.ok ? await res.json() : { frames: [] };
      if (!data.frames?.length) { setTlEmpty(true); setTlLoading(false); return; }
      const urls: string[] = [];
      for (const f of data.frames) {
        const r = await apiFetch(f.url);
        if (r.ok) urls.push(URL.createObjectURL(await r.blob()));
      }
      tlUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      tlUrlsRef.current = urls;
      setTlFrames(urls); setTlEmpty(urls.length === 0);
    } catch { setTlEmpty(true); }
    setTlLoading(false);
  }, []);

  const closeTimelapse = useCallback(() => {
    setTlOpen(false);
    tlUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    tlUrlsRef.current = []; setTlFrames([]);
  }, []);

  useEffect(() => {
    if (!tlOpen || tlFrames.length === 0) return;
    const t = window.setInterval(() => setTlIdx((i) => (i + 1) % tlFrames.length), 1000 / TIMELAPSE_FPS);
    return () => clearInterval(t);
  }, [tlOpen, tlFrames]);

  if (!admin) {
    return <div style={S.wrap}><p style={S.muted}>Accès réservé au staff.</p></div>;
  }

  // Seuil d'attente : toujours celui du serveur (ajustable après le test IRL),
  // 5 min en secours si la route n'a pas encore répondu.
  const dwellThreshold = dwell?.thresholdSeconds ?? 300;

  return (
    <div style={S.wrap}>
      <header style={S.header}>
        <h1 style={S.h1}>Caméras</h1>
        <p style={S.sub}>{cameraName}</p>
      </header>

      {configured === false ? (
        <div style={S.card}>
          <p style={S.muted}>Caméra non configurée pour cet établissement.</p>
          <p style={S.hint}>Une fois les identifiants UniFi Protect fournis et onboardés, le direct s'affichera ici.</p>
        </div>
      ) : (
        <>
          {/* Live view plein cadre. */}
          <div style={S.liveBox}>
            {liveSrc && !liveError ? (
              <img src={liveSrc} alt={`Direct ${cameraName}`} style={S.liveImg} />
            ) : (
              <div style={S.livePlaceholder}>{liveError ? 'Direct indisponible' : 'Connexion au direct…'}</div>
            )}
            <div style={S.liveDot}><span style={S.dot} /> DIRECT</div>
          </div>

          {/* Quelqu'un patiente en ce moment (au-delà du seuil serveur). */}
          {dwell && dwell.waiting.length > 0 && (
            <div style={S.waitingBanner} role="status">
              <span style={S.waitingIcon}>⏱️</span>
              <div>
                <strong style={S.waitingTitle}>
                  {dwell.waiting.length > 1
                    ? `${dwell.waiting.length} personnes en attente`
                    : 'Client en attente'}
                </strong>
                <span style={S.waitingSub}>
                  depuis {dwellMin(dwell.waiting[0].dwellSeconds)} min devant la caméra
                </span>
              </div>
            </div>
          )}

          {/* Compteur Personnes — contextualisé (delta vs hier, pic, affluence). */}
          <div style={S.counters}>
            <span style={S.counterTop}>🚶 Personnes</span>
            <div style={S.counterHeadline}>
              <span style={S.counterVal}>{count?.hour.person ?? '—'}</span>
              <span style={S.counterUnit}>cette heure</span>
              {count?.previousHourSameDay != null && count.hour.person != null && (
                <HourDelta now={count.hour.person} prev={count.previousHourSameDay} />
              )}
            </div>
            <span style={S.counterLbl}>
              {count?.day.person ?? '—'} aujourd'hui
              {count?.peakHour ? ` · pic à ${count.peakHour.hour}h (${count.peakHour.count})` : ''}
            </span>
            {count?.hourlySeries && count.hourlySeries.length > 0 && (
              <div style={S.sparkWrap}>
                <AffluenceSparkline series={count.hourlySeries} />
                <span style={S.sparkCaption}>affluence 8 dernières heures</span>
              </div>
            )}
          </div>

          {/* Actions. */}
          <button style={S.btnPrimary} onClick={captureNow} disabled={capturing}>
            {capturing ? '📸 Capture…' : '📸 Capturer maintenant'}
          </button>
          {tlPreview.length > 0 ? (
            <button style={S.tlPreviewBtn} onClick={openTimelapse} aria-label="Voir le timelapse d'hier">
              <img src={tlPreview[tlPreviewIdx]} alt="" style={S.tlPreviewImg} />
              <div style={S.tlPreviewOverlay}>
                <span style={S.tlPreviewPlay}>▶</span>
                <span style={S.tlPreviewLabel}>Voir le timelapse d'hier</span>
              </div>
              <span style={S.tlPreviewDate}>{parisDate(-1)}</span>
            </button>
          ) : (
            <button style={S.btn} onClick={openTimelapse}>▶ Voir le timelapse d'hier</button>
          )}

          {/* Alertes sécurité (Web Push) hors horaires. */}
          {pushState === 'on' ? (
            <>
              <div style={S.alertOn}>🔔 Alertes de sécurité activées sur cet appareil (nuit 03h–08h)</div>
              <button style={S.btn} onClick={disablePush} disabled={disabling}>
                {disabling ? '🔕 Désactivation…' : '🔕 Désactiver les alertes'}
              </button>
            </>
          ) : pushState === 'busy' ? (
            <button style={S.btn} disabled>🔔 Activation…</button>
          ) : pushState === 'off' ? (
            <button style={S.btn} onClick={() => setPrePrompt(true)}>🔔 Activer les alertes de sécurité</button>
          ) : pushState === 'denied' ? (
            <div style={S.hint}>Notifications bloquées dans le navigateur — autorise-les dans les réglages pour activer les alertes.</div>
          ) : null}

          {/* Carrousel des captures manuelles. */}
          {onDemand.length > 0 && (
            <div style={S.carousel}>
              {onDemand.map((t) => (
                <div key={t.id} style={S.carItem}>
                  <img src={t.blob} alt="Capture" style={S.carImg} />
                  <span style={S.carTime}>{hm(t.capturedAt)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Activité récente. */}
          <h2 style={S.h2}>Activité récente</h2>
          {recent.length === 0 ? (
            <p style={S.hint}>Aucune détection sur les dernières 24 h.</p>
          ) : (
            <div style={S.recentList}>
              {recent.map((g) => (
                <div key={g.hourKey} style={S.recentRow}>
                  <div style={S.recentThumbWrap}>
                    {g.blob ? <img src={g.blob} alt="" style={S.recentThumb} /> : <div style={S.recentThumbEmpty}>—</div>}
                  </div>
                  <div style={S.recentBody}>
                    <div style={S.recentTop}>
                      <span style={S.recentHour}>{hourLabel(g.hourKey)}</span>
                      <span style={S.recentBadge}>{g.count} détection{g.count > 1 ? 's' : ''}</span>
                    </div>
                    <span style={S.recentRange}>{hm(g.first)} → {hm(g.last)}</span>
                    {/* Attente la plus longue de l'heure, si elle a franchi le seuil. */}
                    {g.maxDwellSeconds != null && g.maxDwellSeconds >= dwellThreshold && (
                      <span style={S.waitBadge}>⏱️ En attente {dwellMin(g.maxDwellSeconds)} min</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tlOpen && (
            <div style={S.tlModal} onClick={closeTimelapse}>
              <div style={S.tlInner} onClick={(e) => e.stopPropagation()}>
                <div style={S.tlHead}>
                  <strong>Timelapse — {parisDate(-1)}</strong>
                  <button style={S.tlClose} onClick={closeTimelapse}>✕</button>
                </div>
                {tlLoading ? (
                  <div style={S.livePlaceholder}>Génération du timelapse…</div>
                ) : tlEmpty ? (
                  <div style={S.livePlaceholder}>Aucune image enregistrée hier.</div>
                ) : (
                  <>
                    <img src={tlFrames[tlIdx]} alt="Timelapse" style={S.liveImg} />
                    <div style={S.tlBar}>
                      <div style={{ ...S.tlProgress, width: `${((tlIdx + 1) / tlFrames.length) * 100}%` }} />
                    </div>
                    <p style={S.hint}>{tlFrames.length} images · ta journée en {Math.round(tlFrames.length / TIMELAPSE_FPS)} s</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Pre-prompt FR AVANT la popup iOS (best practice + compense l'anglais système). */}
          {prePrompt && (
            <div style={S.tlModal} onClick={() => setPrePrompt(false)}>
              <div style={S.ppCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.ppIcon}>🔔</div>
                <h3 style={S.ppTitle}>Activer les alertes de sécurité</h3>
                <p style={S.ppBody}>
                  Mayday peut vous alerter en cas de présence détectée hors horaires
                  d'ouverture (entre 3h et 8h). Autoriser les notifications ?
                </p>
                <button style={S.btnPrimary} onClick={() => { setPrePrompt(false); enablePush(); }}>Autoriser</button>
                <button style={S.ppLater} onClick={() => setPrePrompt(false)}>Plus tard</button>
              </div>
            </div>
          )}

          {toast && <div style={S.toast}>{toast}</div>}
          <p style={S.rgpd}>Comptage anonyme — pas de reconnaissance faciale ni de suivi individuel.</p>
        </>
      )}
    </div>
  );
}

// Styles inline (POC) — charte staff Mayday (bleu #1464B4).
const S: Record<string, React.CSSProperties> = {
  wrap: { padding: 'max(env(safe-area-inset-top, 0px) + 16px, 47px) max(16px, env(safe-area-inset-right)) 16px max(16px, env(safe-area-inset-left))', maxWidth: 720, margin: '0 auto', color: '#1a1a24' },
  header: { marginBottom: 12 },
  h1: { fontSize: 24, fontWeight: 700, margin: 0 },
  h2: { fontSize: 16, fontWeight: 700, margin: '20px 0 8px', color: '#3c3c50' },
  sub: { margin: '2px 0 0', color: '#6b6b78', fontSize: 14 },
  card: { background: '#f4f4f7', borderRadius: 14, padding: 20 },
  muted: { color: '#6b6b78', fontSize: 15, margin: 0 },
  hint: { color: '#8a8a96', fontSize: 13, marginTop: 8 },
  liveBox: { position: 'relative', borderRadius: 16, overflow: 'hidden', background: '#111', aspectRatio: '16 / 9' },
  liveImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  livePlaceholder: { width: '100%', height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 14 },
  liveDot: { position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: '4px 8px', borderRadius: 8 },
  dot: { width: 8, height: 8, borderRadius: '50%', background: '#ff3b30', display: 'inline-block' },
  counters: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, background: '#fff', border: '1px solid #e6e6ee', borderTop: '3px solid #1464B4', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 3px rgba(20,60,80,.06)' },
  counterTop: { fontSize: 13, fontWeight: 700, color: '#3c3c50' },
  counterHeadline: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  counterVal: { fontSize: 32, fontWeight: 800, lineHeight: 1, color: '#1464B4' },
  counterUnit: { fontSize: 13, color: '#6b6b78', fontWeight: 600 },
  counterLbl: { fontSize: 12.5, color: '#8a8a96' },
  sparkWrap: { marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 },
  sparkCaption: { fontSize: 11, color: '#9a9aa6' },
  btnPrimary: { marginTop: 12, width: '100%', padding: '14px', fontSize: 16, fontWeight: 700, color: '#fff', background: '#1464B4', border: 'none', borderRadius: 12, cursor: 'pointer' },
  btn: { marginTop: 8, width: '100%', padding: '13px', fontSize: 15, fontWeight: 600, color: '#1464B4', background: '#eef4fb', border: '1px solid #cfe0f3', borderRadius: 12, cursor: 'pointer' },
  tlPreviewBtn: { position: 'relative', marginTop: 8, width: '100%', padding: 0, border: '1px solid #cfe0f3', borderRadius: 12, overflow: 'hidden', background: '#111', cursor: 'pointer', aspectRatio: '16 / 9', display: 'block' },
  tlPreviewImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  tlPreviewOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'linear-gradient(180deg, rgba(0,0,0,.1), rgba(0,0,0,.45))' },
  tlPreviewPlay: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,.92)', color: '#1464B4', fontSize: 15, paddingLeft: 3 },
  tlPreviewLabel: { color: '#fff', fontSize: 15, fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,.5)' },
  tlPreviewDate: { position: 'absolute', bottom: 8, right: 10, fontSize: 11, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,.55)', padding: '2px 7px', borderRadius: 6 },
  waitingBanner: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, background: '#fff6e8', border: '1px solid #f3d4a0', borderLeft: '4px solid #d98518', borderRadius: 12, padding: '11px 14px' },
  waitingIcon: { fontSize: 22, lineHeight: 1 },
  waitingTitle: { display: 'block', fontSize: 14.5, fontWeight: 800, color: '#8a4f07' },
  waitingSub: { fontSize: 12.5, color: '#9a6a2a' },
  waitBadge: { display: 'inline-block', marginTop: 5, fontSize: 11.5, fontWeight: 700, color: '#8a4f07', background: '#fdf0dc', border: '1px solid #f3d4a0', padding: '2px 8px', borderRadius: 999 },
  alertOn: { marginTop: 8, padding: '11px 14px', fontSize: 13.5, fontWeight: 600, color: '#1c6b3f', background: '#e9f7ef', border: '1px solid #bfe6cf', borderRadius: 12, textAlign: 'center' },
  carousel: { display: 'flex', gap: 8, overflowX: 'auto', marginTop: 12, paddingBottom: 4 },
  carItem: { position: 'relative', flex: '0 0 auto' },
  carImg: { width: 112, height: 63, objectFit: 'cover', borderRadius: 8, display: 'block', border: '1px solid #e6e6ee' },
  carTime: { position: 'absolute', bottom: 3, right: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,.55)', padding: '1px 4px', borderRadius: 4 },
  recentList: { display: 'flex', flexDirection: 'column', gap: 8 },
  recentRow: { display: 'flex', gap: 12, alignItems: 'center', background: '#fff', border: '1px solid #e6e6ee', borderRadius: 12, padding: 8 },
  recentThumbWrap: { flex: '0 0 auto' },
  recentThumb: { width: 72, height: 48, objectFit: 'cover', borderRadius: 8, display: 'block' },
  recentThumbEmpty: { width: 72, height: 48, borderRadius: 8, background: '#f0f0f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' },
  recentBody: { flex: 1, minWidth: 0 },
  recentTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  recentHour: { fontSize: 15, fontWeight: 700, color: '#3c3c50' },
  recentBadge: { fontSize: 12, fontWeight: 700, color: '#1464B4', background: '#eef4fb', padding: '2px 8px', borderRadius: 999 },
  recentRange: { fontSize: 12.5, color: '#8a8a96' },
  toast: { position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', background: '#1464B4', color: '#fff', padding: '10px 18px', borderRadius: 999, fontSize: 14, fontWeight: 600, zIndex: 1100, boxShadow: '0 4px 12px rgba(20,60,80,.25)' },
  rgpd: { marginTop: 16, fontSize: 12, color: '#9a9aa6', textAlign: 'center' },
  ppCard: { width: '100%', maxWidth: 360, background: '#fff', borderRadius: 18, padding: '22px 20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,.3)' },
  ppIcon: { fontSize: 40, marginBottom: 6 },
  ppTitle: { fontSize: 18, fontWeight: 800, margin: '0 0 8px', color: '#1a1a24' },
  ppBody: { fontSize: 14, lineHeight: 1.5, color: '#5b5b6a', margin: '0 0 18px' },
  ppLater: { marginTop: 8, width: '100%', padding: '12px', fontSize: 15, fontWeight: 600, color: '#8a8a96', background: 'transparent', border: 'none', cursor: 'pointer' },
  tlModal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  tlInner: { width: '100%', maxWidth: 720, background: '#000', borderRadius: 16, overflow: 'hidden' },
  tlHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', color: '#fff' },
  tlClose: { background: 'transparent', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' },
  tlBar: { height: 4, background: 'rgba(255,255,255,.2)' },
  tlProgress: { height: '100%', background: '#1464B4', transition: 'width .1s linear' },
};
